import { PINBALL_FIELD as ORIGINAL_PINBALL_FIELD } from './field-maps';
import type { FieldObstacle, FieldPoint, FieldRect, FieldShape, FieldSpec } from './field';

/** v3 핀볼은 기존 엔진 좌표를 1.8배 확장한다. 공 반지름과 전역 물리는 유지. */
export const PINBALL_EXPANSION = 1.8;
const S = PINBALL_EXPANSION;
const point = ({ x, y }: FieldPoint): FieldPoint => ({ x: x * S, y: y * S });
const rect = (value: FieldRect): FieldRect => ({ ...point(value), w: value.w * S, h: value.h * S });

function shape(value: FieldShape): FieldShape {
  switch (value.kind) {
    case 'room': return { ...value, ...rect(value), r: (value.r ?? 18) * S };
    case 'disc': return { ...value, ...point(value), r: value.r * S };
    case 'ellipse': return { ...value, ...point(value), rx: value.rx * S, ry: value.ry * S };
    case 'poly': return { ...value, points: value.points.map(point) };
    case 'pipe': return { ...value, points: value.points.map(point), width: value.width * S, ...(value.endWidth === undefined ? {} : { endWidth: value.endWidth * S }) };
  }
}

function device(value: FieldObstacle): FieldObstacle {
  switch (value.kind) {
    case 'bumper': return { ...value, ...point(value), r: value.r * S, restitution: .92 };
    // 길어진 플리퍼 끝의 선속도도 제한한다. 원본 각속도를 그대로 쓰면 군집을 벽으로 발사한다.
    case 'flipper': return { ...value, ...point(value), length: value.length * S, thickness: value.thickness * S, swingSeconds: .32 };
    case 'gate': return { ...value, ...rect(value) };
    // 합류 범퍼 아래 관문은 3.1초마다 부드럽게 역회전한다. 순위/공 수와 무관한
    // 시계로 날의 진입각을 바꾸며, 범퍼·외벽과의 기존 통과 여유는 유지한다.
    case 'rotor': return { ...value, ...point({ x: value.x, y: 1430 }), wingLength: 150 * S, thickness: value.thickness * S, hubR: value.hubR * S, reverseEvery: 3.1 };
    default: throw new Error(`핀볼 확장에 정의되지 않은 장치: ${value.kind}`);
  }
}

function pins(row: number, y: number, xs: readonly number[], sink = false): FieldObstacle[] {
  return xs.map((x, index) => ({ kind: 'pin', id: `pb-pin-r${row}-${index}`, ...point({ x, y }), r: 9 * S, sink }));
}

const spawn = ORIGINAL_PINBALL_FIELD.spawn;
const finish = ORIGINAL_PINBALL_FIELD.finish;
if (spawn.kind !== 'rect' || finish.kind !== 'rect' || !ORIGINAL_PINBALL_FIELD.startGate) throw new Error('핀볼 원본 형상이 변경되었습니다.');

export const PINBALL_V3_FIELD: FieldSpec = {
  ...ORIGINAL_PINBALL_FIELD,
  eventScheduling: 'occupancy',
  // 원형 방 아래의 둥근 턱을 넓은 입구로 잇고, 경사면으로 결승선 안까지
  // 수렴시킨다. 바닥 양 끝도 판정 영역 안이므로 지나친 공이 모서리에 남지 않는다.
  shapes: ORIGINAL_PINBALL_FIELD.shapes.map(value => shape(value.kind === 'room' && value.y === 1500
    ? { kind: 'poly', points: [{ x: 356, y: 1478 }, { x: 544, y: 1478 }, { x: 500, y: 1570 }, { x: 400, y: 1570 }] }
    : value)),
  holes: ORIGINAL_PINBALL_FIELD.holes.map(value => shape(value.kind === 'disc' ? { ...value, y: 1430 } : value)),
  gravity: point(ORIGINAL_PINBALL_FIELD.gravity),
  spawn: { ...spawn, ...rect(spawn), pitch: spawn.pitch * S, fill: 'center' },
  startGate: rect(ORIGINAL_PINBALL_FIELD.startGate),
  finish: { ...finish, ...rect(finish) },
  obstacles: [
    // 32 → 26개(18.75% 감소). 중앙 직통 열 대신 7/6/7/6 엇갈린 핀 배치.
    ...pins(1, 420, [130, 237, 343, 450, 557, 663, 770]),
    ...pins(2, 500, [180, 288, 396, 504, 612, 720]),
    ...pins(3, 580, [130, 237, 343, 450, 557, 663, 770]),
    ...pins(4, 660, [170, 250, 330, 570, 650, 730], true),
    ...ORIGINAL_PINBALL_FIELD.obstacles.filter((value) => value.kind !== 'pin').map(device),
    // 빠른 직통/양쪽 우회가 합쳐진 뒤 한 번 더 튕긴다. 순위와 무관한 고정 충돌체다.
    { kind: 'bumper', id: 'pb-final-divider', ...point({ x: 450, y: 1314 }), r: 22 * S, restitution: .86 },
    // 짧은 중앙 드롭은 주기 문을 기다릴 수 있다. 좌우 우회에는 이 문이 없다.
    { kind: 'gate', id: 'pb-center-cycle', ...rect({ x: 374, y: 1038, w: 154, h: 12 }), schedule: { type: 'cycle', period: 3.6, openSeconds: 1.7, phase: 1.3 } },
  ],
  zones: [
    { kind: 'wait', circle: { ...point({ x: 450, y: 1400 }), r: 126 * S } },
    // 마지막 합류/회전 관문에서도 횡방향 흐름이 고착된 공을 풀어 준다.
    { kind: 'pump', rect: rect({ x: 344, y: 1308, w: 80, h: 80 }), ax: 70 * S, ay: 0, period: 1.2 },
    { kind: 'pump', rect: rect({ x: 476, y: 1308, w: 80, h: 80 }), ax: -70 * S, ay: 0, period: 1.2, phase: .6 },
  ],
  // 짧은 실제 낙하 시간에 맞춘 한 번의 타격/문 전환. 전체 레이스를 늘이지 않는다.
  events: ORIGINAL_PINBALL_FIELD.events.map((event) => ({ ...event, duration: event.type === 'drop-close' ? 1.6 : 1.4, window: [event.window[0] * 1.5, event.window[1] * 1.5] })),
  eventZones: ORIGINAL_PINBALL_FIELD.eventZones.map((zone) => ({
    ...zone,
    ...(zone.rect ? { rect: rect(zone.rect) } : {}),
    ...(zone.circle ? { circle: { ...point(zone.circle), r: zone.circle.r * S } } : {}),
  })),
  landmarks: [
    ...ORIGINAL_PINBALL_FIELD.landmarks.map(label => ({ ...label, ...point(label.label === '회전 관문' ? { x: 610, y: 1430 } : label) })),
    { ...point({ x: 540, y: 1332 }), label: '합류 리바운드' },
    { ...point({ x: 450, y: 985 }), label: '중앙 직통 · 주기 문' },
    { ...point({ x: 208, y: 1020 }), label: '왼쪽 우회' },
    { ...point({ x: 694, y: 1020 }), label: '오른쪽 우회' },
  ],
  maxSpeed: 700,
  wallBounce: true,
  rushAt: 90,
  velocityWatchdog: { minSpeed: 8, seconds: 1.2, impulse: 130 },
  eventMinGapSeconds: 2.5,
  eventFinalGuardSeconds: 5,
};
